import { Moment } from 'moment';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'redux-scaffolding-ts';
import { DateTimeService } from 'services/datetime-service';
import { EventFormMode } from 'site/pages/events/event-form/event-form';
import TailwindPausePeriodList from 'site/pages/events/pause-period-list-tw';
import { EventDetailsViewModel, EventFormStore, EventFormViewModel } from 'stores/events/event-form-store';
import EventService, { DatesCalculationRequestDto } from 'stores/events/event-service';
import { isNullOrWhiteSpaces, replaceAll, trim } from 'utils/useful-functions';
//import TailwindSelect from 'widgets/editors/tailwind-select';
import TailwindLocationEditor from 'widgets/bussiness/location-editor-tw';
import TailwindPriorityEditor from 'widgets/bussiness/priority-editor-tw';

interface EventMainDataProps {
  selectedItem?: EventFormViewModel;
  readOnly?: boolean;
  eventFormStore?: EventFormStore;
  //recalculating: (boolean: boolean) => void;
  mode: EventFormMode;
  onChange?: (data: Partial<EventFormViewModel>) => void;
}

const EventMainData: React.FC<EventMainDataProps> = ({ selectedItem, readOnly, eventFormStore, mode }) => {
  // const initWarnings = (selectedItem: EventFormViewModel) => {
  //   const warnings = [];
  //   if (selectedItem.calculatedEventDurationHours === 0) {
  //     warnings.push({
  //       type: 'DURATION',
  //       text: t('There is no default calculated duration for this event')
  //     });
  //   }

  //   return warnings;
  // };

  const { t } = useTranslation();
  const [event, setEvent] = useState<EventFormViewModel>(selectedItem);
  const [startDate /*, setStartDate*/] = useState<Moment>(DateTimeService.toMoment(selectedItem?.startDate));
  const [endDate /*, setEndDate*/] = useState<Moment>(DateTimeService.toMoment(selectedItem?.endDate));
  const [plannedDuration /*, setPlannedDuration*/] = useState<string>(selectedItem?.plannedDuration);
  //const [warnings, setWarning] = useState<{ type: 'DURATION' | 'CALCULATION'; text: string }[]>(initWarnings(selectedItem));
  const borderColor = selectedItem.eventType.eventsColor;

  const eventsService: EventService = new EventService();

  const handleOnChange = (value: any, property: keyof EventFormViewModel) => {
    let changes: Partial<EventFormViewModel> = { fromScheduler: false };
    changes[property as string] = value;

    if (DateTimeService.isCoherent(value) && (property === 'startDate' || property === 'endDate')) {
      const dateMoment = DateTimeService.toMoment(value);
      const current = property === 'startDate' ? startDate : endDate;
      if (property === 'startDate' && dateMoment.isAfter(endDate)) changes.endDate = value;
      if (dateMoment && current.diff(dateMoment, 'days') !== 0) changes.shouldRecalculate = true;
      else changes.shouldRecalculate = false;
    }
    if (property === 'plannedDuration') {
      if (plannedDuration !== value) changes.shouldRecalculate = true;
      else changes.shouldRecalculate = false;
    }

    const eventCopy = { ...event, ...changes };
    setEvent(eventCopy);
    eventFormStore.change(changes);
  };

  const handleOnEventDetailsChange = (property: keyof EventDetailsViewModel, value: any) => {
    let eventDetails = { ...event?.eventDetails };
    eventDetails[property as string] = value;
    const eventCopy = { ...event, eventDetails };
    setEvent(eventCopy);
    eventFormStore.change({ eventDetails });
  };

  const getDatesCalculationRequestDto = (recalculate: 'StartDate' | 'EndDate' | 'Duration'): DatesCalculationRequestDto => {
    const data = eventFormStore.state.item;
    return {
      startDate: recalculate === 'StartDate' ? null : data.startDate,
      endDate: recalculate === 'EndDate' ? null : data.endDate,
      durationDays: recalculate === 'Duration' ? null : parseInt(trim(replaceAll(data?.plannedDuration || '', 'w/d', '')) || '0'),
      locationId: data.eventDetails?.location?.id,
      pausePeriods: data.pausePeriods || []
    };
  };

  const canRecalculatePlannedDuration = !readOnly && !isNullOrWhiteSpaces(event.endDate) && !isNullOrWhiteSpaces(event.startDate);

  const performCalculation = (recalculate: 'StartDate' | 'EndDate' | 'Duration', setValues = true) => {
    const dto = getDatesCalculationRequestDto(recalculate);
    //recalculating(true);
    eventsService
      .calculateEventDates(dto)
      .then(v => {
        const stateChanges: any = {};
        const data: EventFormViewModel = { ...eventFormStore.state.item };
        const eventCopy = { ...event };

        if (v?.isSuccess && v?.item != null && setValues) {
          data.shouldRecalculate = false;
          data.startDate = v.item.startDate;
          data.endDate = v.item.endDate;
          data.plannedDuration = '' + v.item.durationDays;

          stateChanges.startDate = DateTimeService.toMoment(v.item.startDate);
          stateChanges.endDate = DateTimeService.toMoment(v.item.endDate);
          stateChanges.plannedDuration = '' + v.item.durationDays;

          eventCopy.startDate = stateChanges.startDate;
          eventCopy.endDate = stateChanges.endDate;
          eventCopy.plannedDuration = stateChanges.plannedDuration;
        }
        // stateChanges.warnings = [
        //   ...this.state.warnings.filter(x => x.type !== 'CALCULATION'),
        //   ...(v?.messages || []).map(x => ({ text: x.body, type: 'CALCULATION' } as { type: 'DURATION' | 'CALCULATION'; text: string }))
        // ];
        //
        setEvent(eventCopy);
        eventFormStore.change(data);
        //recalculating(false);
        //});
      })
      .catch(e => {
        console.error(e?.response?.data?.messages);
      });
  };

  return (
    <div className="mb-5 space-y-1">
      <div className="flex gap-3 mb-3">
        <p className="text-sm text-gray-700 font-medium">{event.status.toString()}</p>

        <div className="flex items-center gap-4">
          <label className="block text-sm font-medium text-gray-700">{t('Locked')}</label>
          <div className="flex items-center">
            <input
              id="default-checkbox"
              type="checkbox"
              value=""
              checked={event.priorityLevel === 'P1' || event.locked}
              className="w-4 h-4 text-black-600 bg-gray-100 border-gray-300 rounded-sm focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
              onChange={() => {}}
            />
          </div>
        </div>
      </div>

      <div className="flex items-start gap-4">
        <div className="flex-shrink-0" style={{ width: '14px', height: '14px', borderRadius: '50%', backgroundColor: borderColor }} />
        <h2 className="text-xl font-bold">{`${event.friendlyId} - ${event.title}`}</h2>
      </div>

      <p className="text-sm text-gray-700 font-medium">{`${t('Event Type')}: ${event.eventType.name}`}</p>
      <p className="text-sm text-gray-700 font-medium">{t('Event Dates')}:</p>
      <div className="flex gap-3 mb-3">
        <input
          type="date"
          className="w-full p-2 border border-gray-300 rounded"
          placeholder="From"
          disabled={readOnly}
          value={DateTimeService.toDateInputString(event.startDate, 'YYYY-MM-DD')}
          onChange={e => handleOnChange(e.target.value, 'startDate')}
        />
        <input
          type="date"
          className="w-full p-2 border border-gray-300 rounded"
          placeholder="To"
          disabled={readOnly}
          value={DateTimeService.toDateInputString(event.endDate, 'YYYY-MM-DD')}
          onChange={e => handleOnChange(e.target.value, 'endDate')}
        />
      </div>
      <div className="flex gap-3 mb-3">
        <div className="mb-3">
          <label className="block text-sm text-gray-700 mb-1">{t('Event Duration')}:</label>
          <div className="flex flex-col">
            <input
              type="button"
              className="w-100 p-2 border border-gray-300 rounded"
              placeholder={t('Event Duration')}
              disabled
              value={`${event.plannedDuration} w/d`}
            />
            {canRecalculatePlannedDuration && (
              <span className="recalc-command" onClick={() => performCalculation('Duration')}>
                {t('Recalculate')}
              </span>
            )}
          </div>
        </div>
        <div className="mb-3">
          <label className="block text-sm text-gray-700 mb-1">{t('Calculated')}:</label>
          <input
            type="button"
            className="w-100 p-2 border border-gray-300 rounded"
            placeholder={t('Calculated')}
            disabled
            value={`${event?.calculatedEventDurationHours ? Math.round(event.calculatedEventDurationHours / 8) : 0} w/d`}
          />
        </div>
      </div>
      {event.hasRequestDetails && (event.eventDetails?.location || !readOnly) && (
        <div className="mb-3">
          <label className="block text-sm text-gray-700 mb-1">{t('Event Location')}</label>
          <TailwindLocationEditor
            readOnly={readOnly}
            className="w-full border border-gray-300 rounded px-2 py-1"
            value={event.eventDetails?.location}
            nullable
            clearable
            onChange={location => handleOnEventDetailsChange('location', location)}
          />
        </div>
      )}
      {event.hasRequestDetails && (event.eventDetails?.priority || !readOnly) && (
        <div className="mb-3">
          <label className="block text-sm text-gray-700 mb-1">{t('Priority')}</label>
          <TailwindPriorityEditor
            readOnly={readOnly}
            className="w-full border border-gray-300 rounded px-2 py-1"
            value={event.eventDetails?.priority}
            nullable
            clearable
            onChange={priority => handleOnEventDetailsChange('priority', priority)}
          />
        </div>
      )}
      <div className="mb-3">
        <label className="block text-sm text-gray-700 mb-1">{t('Pause Period')}</label>
        <TailwindPausePeriodList
          onEditMode={!readOnly}
          onChange={periods => handleOnChange(periods, 'pausePeriods')}
          pausePeriods={event.pausePeriods || []}
          startDate={event.startDate}
          endDate={event.endDate}
        />
      </div>

      {event.status && (event.status.toString() === 'Draft' || event.status.toString() === 'Planned') && event.warningMessages.length > 0 && (
        <div className="mb-3">
          <label className="block text-sm text-gray-700 mb-1">
            ‼️
            <div>
              {event.warningMessages.map(
                (messageToShow, index) =>
                  messageToShow && (
                    <p key={'warning-' + index} className="modal__action-warning__text">
                      {t(messageToShow)}
                    </p>
                  )
              )}
            </div>
          </label>
        </div>
      )}
    </div>
  );
};

export default connect(['eventFormStore', EventFormStore])(EventMainData);
